import { useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { GridColDef } from "@mui/x-data-grid-pro";
import { LDFlagSet } from 'launchdarkly-react-client-sdk';

// Contexts
import { withTranslate, withTenant } from '../../core/TenantProvider';
import withFlags from '../../core/FlagsProvider';
// Components, atoms, molecules, organisms
import ClientVBANDetailsPanel from './ClientVBANDetailsPanel';
import APButton from '../../components/atoms/APButton';
import APDataTable from '../../components/atoms/APDataTable/APDataTable'
// Files in the same folder
import {
    ClientManagementContent,
    ClientManagementHeader,
    ClientManagementTableWrapper,
    ClientManagementWrapper
} from './styles/ClientManagementStyledComponents';
import { getClientManagementColumns } from './mappingDefinition/columnMappingDefinition';
// Redux
import { ReducerState } from '../../redux/modules';
import { actions as ClientManagementActions } from './../../redux/modules/clientManagement';
// Types
import { ClientDetails } from '../../redux/modules/clientManagement/reducer';
import { AffiliateDetailsAttributes } from '../../core/types/AffiliateDetails';
// Utils
import { TriggerGoogleAnalytics4Tag } from '../../core/utils/GoogleAnalyticTag';
import { GoogleAnalytics4Tags } from '../../core/utils/GoogleAnalyticTag/GoogleAnalytics4Tags';
// Constants
import { CLIENT_BANK_DETAILS_CSV_FILE_NAME } from '../../core/utils/Constants/Constants';
import { useNavigate } from 'react-router-dom';


interface ClientManagementProps {
    translate: (key: string) => string;
    getClientDetails: Function;
    clientDetailsLoadingState: string;
    clientDetailsResult: ClientDetails[];
    flags?: LDFlagSet;
    affiliateDetails: AffiliateDetailsAttributes;
    forTestingCall?: boolean,
}

const ClientManagementContainer = (props: ClientManagementProps) => {
    const {
        translate,
        getClientDetails,
        clientDetailsLoadingState,
        clientDetailsResult,
        flags = {},
        affiliateDetails,
        forTestingCall = false
    } = props;

    const navigate = useNavigate();

    useEffect(() => {
        !forTestingCall && getClientDetails(affiliateDetails);

        TriggerGoogleAnalytics4Tag(
            GoogleAnalytics4Tags.clientManagement.name,
            GoogleAnalytics4Tags.eventAction.load,
            GoogleAnalytics4Tags.clientManagement.load.clientManagementPageLoad
        );
    }, [getClientDetails]);

    const columnsData: GridColDef[] = getClientManagementColumns(translate);

    const handleRegisterClientClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        navigate('/register_client')
    }
    const shouldEnableRAC: boolean = flags?.enableRegisterClientFlow ?? true;

    return (
        <ClientManagementWrapper>
            <ClientManagementContent>
                <ClientManagementHeader>
                    <h1>{translate('clientmanagement.header.title')}</h1>
                    {shouldEnableRAC &&
                        <APButton
                            variant="contained"
                            onClick={handleRegisterClientClick}
                            color="primary"
                        >
                            {translate('clientmanagement.header.registerclientbutton.label')}
                        </APButton>}
                </ClientManagementHeader>
                <ClientManagementTableWrapper>
                    <APDataTable
                        columnsData={columnsData}
                        rowsData={clientDetailsResult}
                        DetailPanelComponent={ClientVBANDetailsPanel}
                        type="stripped"
                        dataLoading={clientDetailsLoadingState === 'LOADING'}
                        csvFileName={CLIENT_BANK_DETAILS_CSV_FILE_NAME}
                        tableName="clientManagement"
                    />
                </ClientManagementTableWrapper>
            </ClientManagementContent>
        </ClientManagementWrapper>
    )
};

const mapStateToProps = (state: ReducerState) => ({
    clientDetailsLoadingState: state.clientManagement.clientDetailsLoadingState,
    clientDetailsResult: state.clientManagement.clientDetailsResult,
    affiliateDetails: state.user.affiliateDetails
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
    getClientDetails: ClientManagementActions.getClientDetails
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(
    withFlags(
        withTenant(
            withTranslate(
                (ClientManagementContainer)
            )
        )
    )
)